// Live updates are WebSocket-driven, with no polling. `job_fit.*` reaches
// everyone who may see the candidate, so two people with the same candidate
// open see the run start and finish together. The one gap the socket can't
// cover is events missed while it was down — `useLiveResource` refetches on
// reconnect and on a detected gap, rather than on a timer.
//
// The numeric fit score is deliberately NOT rendered. It is a different
// question from the per-job resume match score shown elsewhere ("which jobs
// suit this candidate" vs "how well does this candidate suit this job"), and
// two similar-looking numbers side by side read as a bug. The band
// (high/medium/low) plus the matched/missing evidence carries the signal; the
// score is still used, invisibly, to rank the list.
export const LEVELS = ["high", "medium", "low"];
export const LEVEL_LABEL = { high: "Strong fit", medium: "Moderate fit", low: "Weak fit" };

// Theme-aware tones (see src/themes/styles/tones.js) — plain Tailwind palette
// classes like `text-red-400` only read correctly in the dark theme.
//
// `text` is for icons and the accent stripe only. Labels use text-text-primary:
// measured against these soft fills, tone-on-tone-soft is 3.3-4.2:1 in the light
// theme (tone-emerald is the worst at 3.32), which fails WCAG AA for text this
// size. text-text-primary on the same fills is 14.5-15.9:1 in both themes, and
// the fill, border and icon still carry the colour coding.
export const LEVEL_TONE = {
  high: {
    text: "text-tone-emerald",
    bg: "bg-tone-emerald-soft",
    border: "border-tone-emerald/40",
    stripe: "bg-tone-emerald",
  },
  medium: {
    text: "text-tone-amber",
    bg: "bg-tone-amber-soft",
    border: "border-tone-amber/40",
    stripe: "bg-tone-amber",
  },
  low: {
    text: "text-tone-rose",
    bg: "bg-tone-rose-soft",
    border: "border-tone-rose/40",
    stripe: "bg-tone-rose",
  },
};

/** Same bands as the backend's apply_score_bands (0-49 low / 50-79 medium / 80-100 high). */
export const levelFromScore = (score) => {
  if (!Number.isFinite(score)) return "low";
  if (score >= 80) return "high";
  if (score >= 50) return "medium";
  return "low";
};

export const resolveLevel = (job) => {
  const level = String(job?.fit_level || "").toLowerCase();
  return LEVELS.includes(level) ? level : levelFromScore(Number(job?.fit_score));
};
